import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bell,
  CheckCheck,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MailOpen,
  RefreshCw,
} from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";
import { UserDataContext } from "../context/User";

const statusOptions = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
];

const kindOptions = [
  { key: "", label: "Everything" },
  { key: "message", label: "Messages" },
  { key: "notification", label: "Updates" },
];

const Notifications = () => {
  const {
    markAllNotificationsRead,
    markNotificationRead,
    refreshNotifications,
    unreadNotificationCount,
  } = useContext(UserDataContext);

  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 12,
  });

  const query = useMemo(() => {
    const params = new URLSearchParams({
      status,
      page: String(page),
      limit: "12",
    });
    if (kind) params.set("kind", kind);
    return params.toString();
  }, [kind, page, status]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setNotice({ type: "", message: "" });

    try {
      const { data } = await axios.get(`/api/notifications?${query}`, {
        withCredentials: true,
      });

      if (data.success) {
        setNotifications(data.notifications || []);
        setPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          total: 0,
          limit: 12,
        });
      }
    } catch (error) {
      setNotice({
        type: "error",
        message: error?.response?.data?.message || "Could not load notifications.",
      });
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const openNotificationLink = (link) => {
    if (!link) return;
    if (!link.startsWith("/") && !/^https?:\/\//i.test(link)) return;

    if (/^https?:\/\//i.test(link)) {
      window.location.href = link;
      return;
    }

    window.location.href = link.startsWith("/lwbl") ? link : `/lwbl${link}`;
  };

  const handleMarkRead = async (notification) => {
    await markNotificationRead(notification.id);
    setNotifications((items) => {
      if (status === "unread") {
        return items.filter((item) => item.id !== notification.id);
      }
      return items.map((item) =>
        item.id === notification.id
          ? { ...item, readAt: item.readAt || new Date().toISOString() }
          : item
      );
    });
    refreshNotifications?.();
  };

  const handleOpen = async (notification) => {
    if (!notification.readAt) {
      await handleMarkRead(notification);
    }
    openNotificationLink(notification.link);
  };

  const handleReadAll = async () => {
    await markAllNotificationsRead();
    setNotifications((items) => {
      if (status === "unread") return [];
      return items.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString(),
      }));
    });
    setNotice({ type: "success", message: "All notifications marked as read." });
  };

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const changeKind = (nextKind) => {
    setKind(nextKind);
    setPage(1);
  };

  return (
    <div>
      <Header />
      <Banner backgroundImage={BannerImg} heading="Notifications" minHeight="40vh" padding="80px 0" />

      <section className="main-lwbl rounded-[30px] bg-[#F9FAFB] px-4 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <Bell size={24} />
              </div>
              <div>
                <p className="font-outfit text-sm font-semibold uppercase tracking-[0.18em] text-cyan-600">
                  Member messages
                </p>
                <h2 className="mt-1 font-cinzel text-2xl font-bold text-gray-900">
                  All Notifications
                </h2>
                <p className="mt-1 font-outfit text-sm text-gray-500">
                  Review messages from the LWBL team, account updates, and support replies.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={fetchNotifications}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:border-cyan-300 hover:text-cyan-700"
              >
                <RefreshCw size={15} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleReadAll}
                disabled={unreadNotificationCount <= 0}
                className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-outfit text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckCheck size={15} />
                Mark all read
              </button>
            </div>
          </div>

          {notice.message && (
            <div
              className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
                notice.type === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
            >
              {notice.message}
            </div>
          )}

          <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => changeStatus(option.key)}
                  className={`rounded-full px-4 py-2 font-outfit text-sm font-semibold transition-colors ${
                    status === option.key
                      ? "bg-cyan-500 text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-cyan-50 hover:text-cyan-700"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {kindOptions.map((option) => (
                <button
                  key={option.key || "all-kind"}
                  type="button"
                  onClick={() => changeKind(option.key)}
                  className={`rounded-full px-4 py-2 font-outfit text-sm font-semibold transition-colors ${
                    kind === option.key
                      ? "bg-gray-900 text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-cyan-400 border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-6 py-20 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-300">
                  <Bell size={30} />
                </div>
                <h3 className="font-cinzel text-xl font-bold text-gray-800">
                  No notifications found
                </h3>
                <p className="mx-auto mt-2 max-w-sm font-outfit text-sm text-gray-500">
                  New messages and account updates will appear here.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => {
                  const unread = !notification.readAt;
                  const Icon = notification.kind === "message" ? MailOpen : Bell;

                  return (
                    <article
                      key={notification.id}
                      className={`p-5 transition-colors ${unread ? "bg-cyan-50/50" : "bg-white"}`}
                    >
                      <div className="flex flex-col gap-4 md:flex-row md:items-start">
                        <div
                          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl ${
                            unread ? "bg-cyan-100 text-cyan-700" : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          <Icon size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-cinzel text-lg font-bold text-gray-900">
                                  {notification.title}
                                </h3>
                                {unread && (
                                  <span className="rounded-full bg-cyan-500 px-2.5 py-1 font-outfit text-[11px] font-bold text-white">
                                    Unread
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 font-outfit text-sm leading-6 text-gray-600">
                                {notification.message}
                              </p>
                            </div>
                            <span className="flex-shrink-0 font-outfit text-xs text-gray-400">
                              {formatDateTime(notification.createdAt)}
                            </span>
                          </div>
                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            {notification.link && (
                              <button
                                type="button"
                                onClick={() => handleOpen(notification)}
                                className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
                              >
                                <ExternalLink size={14} />
                                Open
                              </button>
                            )}
                            {unread ? (
                              <button
                                type="button"
                                onClick={() => handleMarkRead(notification)}
                                className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:border-cyan-300 hover:text-cyan-700"
                              >
                                <CheckCircle size={14} />
                                Mark read
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-4 py-2 font-outfit text-sm font-semibold text-gray-500">
                                <CheckCircle size={14} />
                                Read
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="mt-6 flex flex-col items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm sm:flex-row">
              <p className="font-outfit text-sm text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={pagination.currentPage <= 1}
                  onClick={() => setPage((current) => Math.max(current - 1, 1))}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 font-outfit text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft size={15} />
                  Previous
                </button>
                <button
                  type="button"
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 font-outfit text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Notifications;
